import React from 'react'
import { Link } from 'react-router-dom'
import { Phone, Mail, MapPin, Shield, Briefcase, Image as ImageIcon, FileText } from 'lucide-react'

export default function Footer() {
  const currentYear = new Date().getFullYear()

  return (
    <footer className="bg-silver-300 border-t border-silver-400 text-slate-800 text-sm">
      {/* Main Footer Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 md:gap-12">
          
          {/* Company Info */}
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <img
                src="https://assets.auxodev.dk/assets/e3c28b93-c07f-41a2-89fa-cab890739d18/e8092bc9-b1f6-449e-8c9a-83e82880648c.png"
                alt="JP Stål og Rør ApS Logo"
                className="h-14 w-auto object-contain"
              />
            </div>
            <p className="text-xs text-slate-700 leading-relaxed">
              Professionelt smedefirma med speciale i certifikatsvejsning, fjernvarmesvejsning og muffemontering. Vi leverer kompromisløs kvalitet til erhvervskunder, industri og entreprenører.
            </p>
            <div className="pt-2">
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold bg-amber-500/20 text-slate-950 border border-amber-500/40 uppercase tracking-wider">
                Kun Erhverv (B2B)
              </span>
            </div>
          </div>

          {/* Contact Details */}
          <div className="space-y-4">
            <h4 className="text-sm font-bold uppercase tracking-wider text-slate-950">Kontakt os</h4>
            <ul className="space-y-3 text-xs">
              <li className="flex items-start gap-2.5">
                <MapPin className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
                <span className="leading-relaxed">
                  Tjørnedevej 127,<br />
                  4340 Tølløse
                </span>
              </li>
              <li className="flex items-center gap-2.5">
                <Phone className="w-4 h-4 text-amber-600 shrink-0" />
                <a href="tel:+4524460485" className="hover:text-slate-950 transition-colors duration-200 whitespace-nowrap font-medium">
                  (+45) 24 46 04 85
                </a>
              </li>
              <li className="flex items-center gap-2.5">
                <Mail className="w-4 h-4 text-amber-600 shrink-0" />
                <a href="mailto:jp@jpstaal.dk" className="hover:text-slate-950 transition-colors duration-200 whitespace-nowrap font-medium">
                  jp@jpstaal.dk
                </a>
              </li>
              <li className="text-slate-600 font-medium pt-1">
                CVR-nr: 40160140
              </li>
            </ul>
          </div>

          {/* Navigation Links */}
          <div className="space-y-4">
            <h4 className="text-sm font-bold uppercase tracking-wider text-slate-950">Sider</h4>
            <ul className="space-y-2 text-xs font-bold uppercase tracking-wider">
              <li>
                <Link to="/" className="hover:text-slate-950 transition-colors duration-200">Forside</Link>
              </li>
              <li>
                <Link to="/jeg-tilbyder" className="hover:text-slate-950 transition-colors duration-200">Jeg tilbyder</Link>
              </li>
              <li>
                <Link to="/kontakt" className="hover:text-slate-950 transition-colors duration-200">Kontakt</Link>
              </li>
              <li>
                <Link to="/galleri" className="hover:text-slate-950 transition-colors duration-200 flex items-center gap-1.5">
                  <ImageIcon className="w-3.5 h-3.5 text-slate-600" />
                  Galleri &amp; Referencer
                </Link>
              </li>
              <li>
                <Link to="/job" className="hover:text-slate-950 transition-colors duration-200 flex items-center gap-1.5">
                  <Briefcase className="w-3.5 h-3.5 text-slate-600" />
                  Job hos os
                </Link>
              </li>
              <li>
                <Link to="/gdpr" className="hover:text-slate-950 transition-colors duration-200 flex items-center gap-1.5">
                  <Shield className="w-3.5 h-3.5 text-slate-600" />
                  GDPR Politik
                </Link>
              </li>
            </ul>
          </div>

          {/* Project Links (100% Coverage of Scrape) */}
          <div className="space-y-4">
            <h4 className="text-sm font-bold uppercase tracking-wider text-slate-950">Referencer</h4>
            <ul className="space-y-2 text-xs">
              <li>
                <Link to="/brud-i-farum" className="hover:text-slate-950 transition-colors duration-200 flex items-center gap-1.5">
                  <FileText className="w-3.5 h-3.5 text-slate-600" />
                  <span>Brud i Farum</span>
                </Link>
              </li>
              <li>
                <Link to="/projekt" className="hover:text-slate-950 transition-colors duration-200 flex items-center gap-1.5">
                  <FileText className="w-3.5 h-3.5 text-slate-600" />
                  <span>Projekt (Svejsning)</span>
                </Link>
              </li>
              <li>
                <Link to="/projekt-2" className="hover:text-slate-950 transition-colors duration-200 flex items-center gap-1.5">
                  <FileText className="w-3.5 h-3.5 text-slate-600" />
                  <span>Projekt (Udgravning)</span>
                </Link>
              </li>
              <li>
                <Link to="/projekt-3" className="hover:text-slate-950 transition-colors duration-200 flex items-center gap-1.5">
                  <FileText className="w-3.5 h-3.5 text-slate-600" />
                  <span>Projekt (Muffemontage)</span>
                </Link>
              </li>
              <li>
                <Link to="/projekt-4" className="hover:text-slate-950 transition-colors duration-200 flex items-center gap-1.5">
                  <FileText className="w-3.5 h-3.5 text-slate-600" />
                  <span>Projekt (Certifikatsvejsning)</span>
                </Link>
              </li>
            </ul>
          </div>

        </div>
      </div>

      {/* Bottom Bar */}
      <div className="bg-silver-400/50 border-t border-silver-400 py-6 px-4 md:px-8 text-center text-xs text-slate-800 font-medium">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row justify-between items-center gap-4">
          <p>© {currentYear} JP Stål og Rør ApS. Alle rettigheder forbeholdes. CVR: 40160140.</p>
          <p className="flex items-center gap-1">
            <span>Udviklet til erhverv &amp; industri</span>
            <span className="text-slate-500">•</span>
            <span>B2B-fokuseret</span>
          </p>
        </div>
      </div>
    </footer>
  )
}
